/**
 * PreferencesService — Syncs user settings between localStorage and server DB
 * localStorage is the fast/offline cache, DB is the cross-device sync source of truth.
 * Guests only use localStorage (no API calls).
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { Navigation } from './Navigation.js';

export class PreferencesService {
    static STORAGE_KEY = 'boomfection_user_prefs';

    /**
     * All preference keys with their localStorage source mapping.
     * lsKey: JSON object key in localStorage, path: property inside the JSON object
     * directKey: standalone localStorage key (value stored directly, not in JSON)
     */
    static PREF_MAP = {
        playerName:      { lsKey: 'boomfection_game_settings', path: 'playerName' },
        colorId:         { lsKey: 'boomfection_game_settings', path: 'colorId' },
        virusType:       { lsKey: 'boomfection_game_settings', path: 'virusType' },
        maxPlayers:      { lsKey: 'boomfection_game_settings', path: 'maxPlayers' },
        subdivisions:    { lsKey: 'boomfection_game_settings', path: 'subdivisions' },
        isPublic:        { lsKey: 'boomfection_game_settings', path: 'isPublic' },
        pillsEnabled:    { lsKey: 'boomfection_game_settings', path: 'pillsEnabled' },
        randomFill:      { lsKey: 'boomfection_game_settings', path: 'randomFill' },
        soundEnabled:    { lsKey: 'boomfection_sound_settings', path: 'enabled' },
        soundVolume:     { lsKey: 'boomfection_sound_settings', path: 'volume' },
        explosionSpeed:  { lsKey: null, directKey: 'boomfection_explosion_speed' },
        showFieldValues: { lsKey: null, directKey: 'boomfection_show_field_values' },
        colorblindMode:  { lsKey: null, directKey: 'boomfection_colorblind_mode' },
        theme:           { lsKey: null, directKey: 'theme' },
        locale:          { lsKey: null, directKey: 'boomfection_locale' },
        slots:           { lsKey: 'boomfection_game_settings', path: 'slots', isJson: true },
    };

    /**
     * Collect all current preferences from localStorage
     * @returns {Object} preference key-value map
     */
    static collectFromLocalStorage() {
        const prefs = {};
        const jsonCache = {};

        for (const [prefKey, mapping] of Object.entries(this.PREF_MAP)) {
            try {
                if (mapping.lsKey) {
                    if (!jsonCache[mapping.lsKey]) {
                        const raw = localStorage.getItem(mapping.lsKey);
                        jsonCache[mapping.lsKey] = raw ? JSON.parse(raw) : null;
                    }
                    const obj = jsonCache[mapping.lsKey];
                    if (obj && mapping.path in obj) {
                        prefs[prefKey] = mapping.isJson ? JSON.stringify(obj[mapping.path]) : obj[mapping.path];
                    }
                } else if (mapping.directKey) {
                    const val = localStorage.getItem(mapping.directKey);
                    if (val !== null) {
                        // Try to parse booleans/numbers
                        if (val === 'true') prefs[prefKey] = true;
                        else if (val === 'false') prefs[prefKey] = false;
                        else if (!isNaN(val) && val.trim() !== '') prefs[prefKey] = Number(val);
                        else prefs[prefKey] = val;
                    }
                }
            } catch {
                // Skip corrupted entries
            }
        }

        return prefs;
    }

    /**
     * Apply preferences from server to localStorage
     * @param {Object} prefs - preference key-value map
     */
    static applyToLocalStorage(prefs) {
        if (!prefs || typeof prefs !== 'object') return;

        // Group updates by lsKey to batch JSON writes
        const jsonUpdates = {};

        for (const [prefKey, value] of Object.entries(prefs)) {
            const mapping = this.PREF_MAP[prefKey];
            if (!mapping) continue;

            try {
                if (mapping.lsKey) {
                    if (!jsonUpdates[mapping.lsKey]) {
                        const raw = localStorage.getItem(mapping.lsKey);
                        jsonUpdates[mapping.lsKey] = raw ? JSON.parse(raw) : {};
                    }
                    jsonUpdates[mapping.lsKey][mapping.path] = value;
                } else if (mapping.directKey) {
                    localStorage.setItem(mapping.directKey, String(value));
                }
            } catch {
                // Skip write errors
            }
        }

        // Write batched JSON objects
        for (const [lsKey, obj] of Object.entries(jsonUpdates)) {
            try {
                localStorage.setItem(lsKey, JSON.stringify(obj));
            } catch {
                // Skip write errors
            }
        }
    }

    /**
     * Upload all current localStorage preferences to the server
     * Used on login (if server has no prefs) or on register
     * @returns {Promise<boolean>} success
     */
    static async syncToServer() {
        if (!Navigation.getUser()) return false;

        const prefs = this.collectFromLocalStorage();
        if (Object.keys(prefs).length === 0) return false;

        try {
            const res = await Navigation.authFetch('/api/v1/preferences', {
                method: 'PUT',
                body: JSON.stringify({ preferences: prefs })
            });
            return res && res.ok;
        } catch {
            return false;
        }
    }

    /**
     * Download preferences from server and apply to localStorage
     * Server is source of truth for logged-in users.
     * If server has no preferences, uploads current localStorage to server.
     * @returns {Promise<boolean>} success
     */
    static async syncFromServer() {
        if (!Navigation.getUser()) return false;

        try {
            const res = await Navigation.authFetch('/api/v1/preferences');
            if (!res || !res.ok) return false;

            const data = await res.json();
            const serverPrefs = data.preferences || {};

            if (Object.keys(serverPrefs).length > 0) {
                // Server has prefs — apply to localStorage (server is source of truth)
                this.applyToLocalStorage(serverPrefs);
                return true;
            } else {
                // Server has no prefs — upload local to server (first-time sync)
                await this.syncToServer();
                return true;
            }
        } catch {
            return false;
        }
    }

    /**
     * Save a single preference key. Writes to localStorage and to server if logged in.
     * Fire-and-forget for server call — never blocks UI.
     * @param {string} key - preference key from PREF_MAP
     * @param {*} value - the value to save
     */
    static save(key, value) {
        if (!this.PREF_MAP[key]) return;

        // Server sync (fire-and-forget)
        if (Navigation.getUser()) {
            Navigation.authFetch('/api/v1/preferences', {
                method: 'PUT',
                body: JSON.stringify({ preferences: { [key]: value } })
            }).catch(() => { /* fire-and-forget */ });
        }
    }

    /**
     * Save multiple preferences at once. Fire-and-forget for server.
     * @param {Object} prefs - key-value map of preferences to save
     */
    static saveMultiple(prefs) {
        if (!prefs || typeof prefs !== 'object') return;

        if (Navigation.getUser()) {
            Navigation.authFetch('/api/v1/preferences', {
                method: 'PUT',
                body: JSON.stringify({ preferences: prefs })
            }).catch(() => { /* fire-and-forget */ });
        }
    }
}
